import { partsRegisterListQuerySchema } from '@chukua/common';
import { Tag } from 'antd';
import { FileX, ShieldAlert } from 'lucide-react';
import { useCallback } from 'react';
import { Navigate, useParams } from 'react-router';
import { DetailPageSkeleton } from '../../../../../shared/components/DetailPageSkeleton';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { PageHeader } from '../../../../../shared/components/PageHeader';
import { withinSection } from '../../../../../shared/components/section-trail';
import { MAINTENANCE_TAB_LABELS, maintenanceTabPath } from '../../../maintenance.paths';
import { PartsRegisterTable } from '../../parts/components/PartsRegisterTable';
import { listPartsRegister } from '../../parts/parts.api';
import { usePartTypeDetail } from '../hooks/use-part-type-detail';
import { formatExpectedLifeDays, formatExpectedLifeKm } from '../policies/expected-life-labels';

const TRAIL = withinSection('maintenance', { label: MAINTENANCE_TAB_LABELS['part-types'], to: maintenanceTabPath('part-types') });

/** One part type: its expected life and every fitting of it across the fleet. Which trucks replaced it too soon is on each truck's Part life tab. */
export function PartTypeDetailPage() {
  const { id = '' } = useParams();
  const state = usePartTypeDetail(id);
  const fetchPage = useCallback((query: Parameters<typeof listPartsRegister>[0]) => listPartsRegister({ ...query, partTypeId: [id] }), [id]);

  if (state.status === 'forbidden') return <Navigate to="/" replace />;
  if (state.status === 'loading') return <DetailPageSkeleton label="Loading part type" breadcrumbs={TRAIL} tabs={1} />;
  if (state.status === 'not_found')
    return <EmptyState icon={FileX} title="Part type not found" description="This part type doesn't exist, or the link is out of date." />;
  if (state.status === 'error') {
    return <EmptyState icon={ShieldAlert} title="Couldn't load this part type" description="Something went wrong loading it. Try reloading the page." />;
  }

  const { partType } = state;
  return (
    <>
      <PageHeader
        breadcrumbs={TRAIL}
        title={partType.name}
        description={`Expected life: ${formatExpectedLifeKm(partType.expectedLifeKm)} · ${formatExpectedLifeDays(partType.expectedLifeDays)}`}
        actions={<Tag color={partType.isActive ? 'success' : 'default'}>{partType.isActive ? 'Active' : 'Inactive'}</Tag>}
      />
      <PartsRegisterTable
        schema={partsRegisterListQuerySchema}
        fetchPage={fetchPage}
        showTruck
        showPartTypeFilter={false}
        emptyDescription="Nothing of this type has been fitted yet."
        searchPlaceholder="Search part, serial, work order, supplier or truck"
      />
    </>
  );
}
